import { DOCUMENT, Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { AppState, AuthService, RedirectLoginOptions } from "@auth0/auth0-angular";
import { Configuration } from "@hydrantid/acm-client";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { map, switchMap, tap, withLatestFrom } from "rxjs/operators";
import { environment } from "../../../configuration";
import { AppUserActions } from "../../modules/shared/app-user/store/actions";
import { CachedDataService } from "../../services/cached-data/cached-data.service";
import { MessageService } from "../../services/message/message.service";
import { RedirectorService } from "../services/redirector.service";
import { AuthActions } from "./actions";

@Injectable()
export class AuthEffects {
    login$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.login),
            switchMap((action) => {
                if (action.connection) {
                    return of(action);
                }
                return this.cachedDataService.getConnectionInfoForSlug("::first").pipe(
                    map((connectionInfo) => {
                        if (connectionInfo) {
                            return { ...action, connection: connectionInfo.connection };
                        }
                        this.messageService.error("Invalid configuration: no connection info found");
                        return undefined;
                    }),
                );
            }),
            switchMap((action) => {
                if (!action) {
                    return of({ type: "Unknown" });
                }
                if (action.redirect) {
                    this.redirectorService.setRedirect(action.redirect);
                } else {
                    this.redirectorService.clearRedirect();
                }
                const params: RedirectLoginOptions<AppState> = {
                    authorizationParams: {
                        redirect_uri: this.document.location.origin,
                        connection: action.connection,
                        login_hint: action.email,
                        useLocalServices: environment.auth0UseLocalServices,
                    },
                };
                this.authService.loginWithRedirect(params);
                return of({ type: "Unknown" });
            }),
        ),
    );

    logout$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthActions.logout),
            map(() => {
                this.authService.logout({ logoutParams: { returnTo: this.document.location.origin + "/logout" } });
                return { type: "Unknown" };
            }),
        ),
    );

    /** Synchronizes Auth0 session changes into ACM's current-user profile state. */
    syncSessionToCurrentUser$ = createEffect(() =>
        this.authService.idTokenClaims$.pipe(
            withLatestFrom(this.authService.user$),
            map(([idToken, authUser]) => {
                if (!idToken) {
                    return AppUserActions.setUser({ appUser: null });
                }
                this.apiConfiguration.credentials = { OAuth2AuthorizationCodeBearer: idToken.__raw };
                return AppUserActions.loadCurrentUser({ authUser });
            }),
        ),
    );

    currentUserLoadFailed$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(AppUserActions.loadCurrentUserFailed),
                tap(({ message }) => {
                    this.redirectorService.errorMsg = message;
                    this.router.navigate(["/logout"]);
                }),
            ),
        { dispatch: false },
    );

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private authService: AuthService,
        private actions$: Actions,
        private apiConfiguration: Configuration,
        private router: Router,
        private redirectorService: RedirectorService,
        private cachedDataService: CachedDataService,
        private messageService: MessageService,
    ) {}
}
